<template>
    <div>
        <md-progress-spinner md-mode="indeterminate"
            v-if="isLoading"></md-progress-spinner>

        <md-card v-else
            class="md-layout-item md-size-100 md-small-size-100">
            <md-card-content>
                <div class="md-layout md-gutter">
                    <div class="md-layout-item md-size-100">
                        <md-field md-clearable
                            :class="{'md-invalid': $v.organization.organization_name.$dirty && $v.organization.organization_name.$invalid}">
                            <label>{{ $t('organizations.name') }}</label>
                            <md-input v-model="organization.organization_name"
                                @input="$v.organization.organization_name.$touch()"></md-input>
                            <span class="md-error"
                                v-show="$v.organization.organization_name.$dirty && $v.organization.organization_name.$invalid">{{ $tc('validations.required-field',1) }}</span>
                        </md-field>

                        <md-field md-clearable
                            :class="{'md-invalid': $v.organization.nipc.$dirty && ($v.organization.nipc.$invalid || $v.organization.nipc.$params.maxLength.max > 12)}">
                            <label>{{ $t('organizations.nipc') }}</label>
                            <md-input v-model="organization.nipc"
                                @input="$v.organization.nipc.$touch()"></md-input>
                            <span class="md-error"
                                v-show="$v.organization.nipc.$dirty && $v.organization.nipc.$invalid">{{ $t('validations.max-length',{num: $v.organization.nipc.$params.maxLength.max}) }}. {{ $tc('validations.required-field',1) }}</span>
                        </md-field>

                        <md-field md-clearable
                            :class="{'md-invalid': $v.organization.phone.$dirty && $v.organization.phone.$invalid}">
                            <label>{{ $t('organizations.phone') }}</label>
                            <md-input v-model="organization.phone"
                                @input="$v.organization.phone.$touch()"></md-input>
                            <span class="md-error"
                                v-show="$v.organization.phone.$dirty && $v.organization.phone.$invalid">{{ $tc('validations.required-field',1) }}</span>
                        </md-field>

                        <md-field md-clearable
                            :class="{'md-invalid': $v.organization.email.$dirty && $v.organization.email.$invalid}">
                            <label>{{ $t('organizations.email') }}</label>
                            <md-input v-model="organization.email"
                                @input="$v.organization.email.$touch()"></md-input>
                            <span class="md-error"
                                v-show="$v.organization.email.$dirty && $v.organization.email.$invalid">{{ $tc('validations.required-field',1) }}</span>
                        </md-field>

                        <md-field md-clearable
                            :class="{'md-invalid': $v.organization.street.$dirty && $v.organization.street.$invalid}">
                            <label>{{ $t('organizations.street') }}</label>
                            <md-input v-model="organization.street"
                                @input="$v.organization.street.$touch()"></md-input>
                            <span class="md-error"
                                v-show="$v.organization.street.$dirty && $v.organization.street.$invalid">{{ $tc('validations.required-field',1) }}</span>
                        </md-field>

                        <md-field md-clearable
                            :class="{'md-invalid': $v.organization.city.$dirty && $v.organization.city.$invalid}">
                            <label>{{ $t('organizations.city') }}</label>
                            <md-input v-model="organization.city"
                                @input="$v.organization.city.$touch()"></md-input>
                            <span class="md-error"
                                v-show="$v.organization.city.$dirty && $v.organization.city.$invalid">{{ $tc('validations.required-field',1) }}</span>
                        </md-field>

                        <md-field md-clearable
                            :class="{'md-invalid': $v.organization.state.$dirty && $v.organization.state.$invalid}">
                            <label>{{ $t('organizations.state') }}</label>
                            <md-input v-model="organization.state"
                                @input="$v.organization.state.$touch()"></md-input>
                            <span class="md-error"
                                v-show="$v.organization.state.$dirty && $v.organization.state.$invalid">{{ $tc('validations.required-field',1) }}</span>
                        </md-field>

                        <md-field md-clearable
                            :class="{'md-invalid': $v.organization.zip_code.$dirty && $v.organization.zip_code.$invalid}">
                            <label>{{ $t('organizations.zip-code') }}</label>
                            <md-input v-model="organization.zip_code"
                                @input="$v.organization.zip_code.$touch()"></md-input>
                            <span class="md-error"
                                v-show="$v.organization.zip_code.$dirty && $v.organization.zip_code.$invalid">{{ $tc('validations.required-field',1) }}</span>
                        </md-field>

                        <md-field :class="{'md-invalid': $v.organization.preferred_delivery_hour.$dirty && $v.organization.preferred_delivery_hour.$invalid}">
                            <flat-pickr v-model="organization.preferred_delivery_hour"
                                :config="dateConfig"
                                class="form-control"
                                :placeholder="$t('organizations.select-time')"></flat-pickr>
                            <span class="md-error"
                                v-show="$v.organization.preferred_delivery_hour.$dirty && $v.organization.preferred_delivery_hour.$invalid">{{ $tc('validations.required-field',1) }}</span>
                        </md-field>
                    </div>

                    <div class="md-layout-item md-size-100">
                        <div class="action-bar">
                            <md-button class="md-raised"
                                @click="close">
                                {{ $t('actions.cancel') }}
                            </md-button>
                            <md-button class="md-raised md-primary"
                                @click="save"
                                :disabled="$v.$invalid">
                                {{ this.isAddMode ? $t('actions.next') : $t('actions.confirm') }}
                            </md-button>
                        </div>
                    </div>
                </div>
            </md-card-content>
        </md-card>
    </div>
</template>

<script>
import _ from 'lodash'
import { required, maxLength, email } from 'vuelidate/lib/validators'
import { DateTime } from 'luxon'
import organizationsService from '@/components/organizations/services/organizations'
import FlatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'

const loadingKeys = {
    creatingItem: 'creating-item',
    fetchingItem: 'fetching-item'
}

export default {
    name: 'OrganizationMaintenance',

    components: {
        FlatPickr
    },

    data () {
        return {
            organization: {
                organization_name: '',
                nipc: '',
                phone: '',
                email: '',
                street: '',
                city: '',
                state: '',
                zip_code: '',
                preferred_delivery_hour: null
            },

            dateConfig: {
                altFormat: 'F j, Y',
                enableTime: true,
                noCalendar: true,
                dateFormat: 'H:i',
                locale: Portuguese
            },

            isViewMode: false,
            isAddMode: false,
            isEditMode: false,
            loadings: [],
            imageData: '',
            logoData: ''
        }
    },

    validations: {
        organization: {
            organization_name: {
                required
            },
            nipc: {
                required,
                maxLength: maxLength(12)
            },
            phone: {},
            email: {
                required,
                email
            },
            street: {
                required
            },
            city: {},
            state: {},
            zip_code: {},
            preferred_delivery_hour: {
                required
            }
        }
    },

    computed: {
        isLoading () {
            return this.isFetchingCategories || this.isCreatingCategories
        },

        isCreatingCategories () {
            return !!this.loadings.filter(
                loading => loading.key === loadingKeys.creatingItem
            ).length
        },

        isFetchingCategories () {
            return !!this.loadings.filter(
                loading => loading.key === loadingKeys.fetchingItem
            ).length
        }
    },

    methods: {
        makeDeliveryDateTime (deliveryDate, deliveryTime) {
            let year = deliveryDate.getFullYear()

            let month = deliveryDate.getMonth() + 1
            if (month < 10) {
                month = '0' + month
            }

            let day = deliveryDate.getDate()
            if (day < 10) {
                day = '0' + day
            }

            let deliveryHours = deliveryTime.split(':')[0]
            if (deliveryHours.length < 2) {
                deliveryHours = '0' + deliveryHours
            }

            let deliveryMinutes = deliveryTime.split(':')[1]
            if (deliveryMinutes.length < 2) {
                deliveryMinutes = '0' + deliveryMinutes
            }

            let deliveryDateTimeString = year + '-' + month + '-' + day + 'T' + deliveryHours + ':' + deliveryMinutes + ':00'
            return new Date(deliveryDateTimeString)
        },

        async save () {
            _.fill(this.loadings, loadingKeys.creatingItem)

            let self = this
            let deliveryDateTime = self.makeDeliveryDateTime(new Date(), self.organization.preferred_delivery_hour)

            let params = {
                organization_id: self.organization.organization_id,
                organization_name: self.organization.organization_name,
                nipc: self.organization.nipc,
                phone: self.organization.phone,
                email: self.organization.email,
                street: self.organization.street,
                city: self.organization.city,
                state: self.organization.state,
                zip_code: self.organization.zip_code,
                preferred_delivery_hour: deliveryDateTime,
                active: self.isAddMode ? 1 : self.organization.active
            }
            // eslint-disable-next-line
            let response

            if (this.isAddMode) {
                // response = await organizationsService.create(params)
                // emit event to parent with params
                this.$emit('neworganization', params)
            } else {
                response = await organizationsService.update(params)
            }

            _.pull(this.loadings, loadingKeys.creatingItem)

            if (!this.isAddMode) {
                this.$router.go(-1)
            }
        },

        async fetchOrganization () {
            _.fill(this.loadings, loadingKeys.fetchingItem)

            let self = this

            let params = {
                filter: {
                    organization_id: self.$route.params.id
                }
            }

            _.fill(this.loadings, loadingKeys.fetchingItem)

            let response = await organizationsService.retrieve(params)
            self.organization = _.cloneDeep(response.data.results[0])

            _.pull(this.loadings, loadingKeys.fetchingItem)
        },

        close () {
            this.$router.go(-1)
        },

        setMode (mode) {
            switch (mode) {
                case 'view-organization':
                    this.isViewMode = true
                    break
                case 'add-organization':
                    this.isAddMode = true
                    break
                case 'edit-organization':
                    this.isEditMode = true
                    break
            }
        }
    },
    created () {
        this.setMode(this.$route.name)

        if (!this.isAddMode) {
            this.fetchOrganization()
        } else {
            this.organization.preferred_delivery_hour = DateTime.local().toLocaleString(DateTime.TIME_24_SIMPLE)
        }
    }
}
</script>
